import { useState, useMemo } from "react"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useQuery } from "convex/react"
import { api } from "@convex/_generated/api"
import type { Id } from "@convex/_generated/dataModel"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Trophy, ChevronRight, Star } from "lucide-react"
import { cn } from "@/lib/utils"
import { EmptyState, CountryCell } from "./-components"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { Button } from "@/components/ui/button"

export const Route = createFileRoute("/(app)/app/results/champions")({
  component: ChampionsResultsPage,
})

type QualifierFilter = "all" | "qualifiers" | "non-qualifiers"

function ChampionsResultsPage() {
  const search = Route.useSearch()
  const navigate = useNavigate()

  const selectedWeek = search.week

  const overallData = useQuery(api.quizzes.handlers.getChampionsLeaderboard, {})

  const games = useQuery(api.quizzes.handlers.getPublishedQuizzesForType, {
    quizType: "champions",
  })

  const gameBreakdown = useQuery(
    api.quizzes.handlers.getChampionsGameBreakdown,
    selectedWeek ? { quizId: selectedWeek as Id<"quizzes"> } : "skip"
  )

  const qualifiers = useQuery(
    api.quizzes.handlers.getChampionQualifiersForGame,
    selectedWeek ? { quizId: selectedWeek as Id<"quizzes"> } : "skip"
  )

  const handleTabChange = (value: string) => {
    void navigate({
      to: "/app/results/champions",
      search: {
        ...search,
        week: value === "overall" ? undefined : value,
      },
    })
  }

  if (overallData === undefined || games === undefined) {
    return <Skeleton className="h-[400px] w-full" />
  }

  const activeTab = selectedWeek ?? "overall"

  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="flex-wrap h-auto gap-1">
          <TabsTrigger value="overall">Overall</TabsTrigger>
          {games.map((game) => (
            <TabsTrigger key={game._id} value={game._id}>
              Game {game.gameNumber}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overall" className="mt-6">
          <ChampionsOverallLeaderboard data={overallData} />
        </TabsContent>

        {games.map((game) => (
          <TabsContent key={game._id} value={game._id} className="mt-6">
            {selectedWeek === game._id ? (
              <ChampionsGameBreakdown
                data={gameBreakdown}
                gameNumber={game.gameNumber}
                qualifiers={qualifiers}
              />
            ) : null}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  )
}

// Overall leaderboard

interface OverallData {
  players: {
    userId: Id<"users">
    name: string
    country: string | null
    gameScores: { quizId: Id<"quizzes">; score: number | null }[]
    totalScore: number
    rank: number
  }[]
  quizzes: { _id: Id<"quizzes">; name: string; gameNumber: number }[]
}

function ChampionsOverallLeaderboard({ data }: { data: OverallData }) {
  const [sortKey, setSortKey] = useState<"total" | "name">("total")
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc")

  const handleSort = (key: "total" | "name") => {
    if (sortKey === key) {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"))
    } else {
      setSortKey(key)
      setSortDir(key === "name" ? "asc" : "desc")
    }
  }

  const sortedPlayers = useMemo(() => {
    return [...data.players].sort((a, b) => {
      if (sortKey === "name") {
        const cmp = a.name.localeCompare(b.name)
        return sortDir === "asc" ? cmp : -cmp
      }
      return sortDir === "desc"
        ? b.totalScore - a.totalScore
        : a.totalScore - b.totalScore
    })
  }, [data.players, sortKey, sortDir])

  if (data.players.length === 0) {
    return (
      <EmptyState
        title="Champions Standings"
        icon={Trophy}
        heading="No Results Yet"
        description="No published Champions game results found."
      />
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Champions Standings</CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead
                className="sticky left-0 bg-background z-10 min-w-[200px] cursor-pointer hover:bg-muted/50"
                onClick={() => handleSort("name")}
              >
                <span className="flex items-center gap-1">
                  Player {sortKey === "name" && (sortDir === "asc" ? "↑" : "↓")}
                </span>
              </TableHead>
              <TableHead
                className="text-right min-w-[80px] font-bold bg-amber-50/50 dark:bg-amber-900/10 cursor-pointer hover:bg-amber-100/50"
                onClick={() => handleSort("total")}
              >
                <span className="flex items-center justify-end gap-1">
                  Total {sortKey === "total" && (sortDir === "asc" ? "↑" : "↓")}
                </span>
              </TableHead>
              {data.quizzes.map((quiz) => (
                <TableHead key={quiz._id} className="text-center min-w-[60px]">
                  #{quiz.gameNumber}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedPlayers.map((player) => (
              <TableRow key={player.userId}>
                <TableCell className="font-medium sticky left-0 bg-background z-10">
                  <span className="flex items-center gap-1">
                    {player.name}
                    <CountryCell country={player.country} showName={false} />
                  </span>
                </TableCell>
                <TableCell className="text-right font-bold tabular-nums bg-amber-50/50 dark:bg-amber-900/10">
                  {player.totalScore}
                </TableCell>
                {player.gameScores.map((game) => (
                  <TableCell
                    key={game.quizId}
                    className="text-center tabular-nums"
                  >
                    {game.score !== null ? (
                      <span>{game.score}</span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}

// Per-game breakdown with expandable rounds

interface GameBreakdownData {
  quiz: { _id: Id<"quizzes">; name: string; totalRounds: number } | null
  players: {
    playerId: Id<"users">
    playerName: string
    country: string | null
    questionScores: {
      questionId: Id<"questions">
      roundNumber: number
      order: number
      isCorrect: boolean
      score: number
    }[]
    totalScore: number
    rank: number
  }[]
  questions: {
    questionId: Id<"questions">
    roundNumber: number
    order: number
    text: string
  }[]
}

interface QualifierEntry {
  userId: Id<"users">
  division: string
  divisionRank: number
}

function ChampionsGameBreakdown({
  data,
  gameNumber,
  qualifiers,
}: {
  data: GameBreakdownData | null | undefined
  gameNumber: number
  qualifiers: QualifierEntry[] | undefined
}) {
  const [expandedRound, setExpandedRound] = useState<number | null>(null)
  const [sortKey, setSortKey] = useState<"total" | "name">("total")
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc")
  const [qualifierFilter, setQualifierFilter] = useState<QualifierFilter>("all")

  const toggleRound = (round: number) => {
    setExpandedRound((prev) => (prev === round ? null : round))
  }

  const handleSort = (key: "total" | "name") => {
    if (sortKey === key) {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"))
    } else {
      setSortKey(key)
      setSortDir(key === "name" ? "asc" : "desc")
    }
  }

  // Build a set of qualifier userIds for quick lookup
  const qualifierUserIds = useMemo(() => {
    if (!qualifiers) return new Set<string>()
    return new Set(qualifiers.map((q) => q.userId))
  }, [qualifiers])

  const hasQualifiers = qualifiers !== undefined && qualifiers.length > 0

  if (data === undefined) {
    return <Skeleton className="h-[400px] w-full" />
  }

  if (!data?.quiz || data.players.length === 0) {
    return (
      <EmptyState
        title={`Game ${gameNumber}`}
        icon={Trophy}
        heading="No Results"
        description="No Champions results found for this game."
      />
    )
  }

  const totalRounds = data.quiz.totalRounds || 1
  const questionsPerRound = Math.ceil(data.questions.length / totalRounds)

  const questionsByRound: Record<number, typeof data.questions> = {}
  for (let r = 1; r <= totalRounds; r++) {
    questionsByRound[r] = data.questions.filter((q) => q.roundNumber === r)
  }

  const playersWithRoundScores = data.players.map((player) => {
    const roundScores: Record<number, number> = {}
    for (let r = 1; r <= totalRounds; r++) {
      roundScores[r] = player.questionScores
        .filter((q) => q.roundNumber === r)
        .reduce((sum, q) => sum + q.score, 0)
    }
    return { ...player, roundScores }
  })

  const sortedPlayers = [...playersWithRoundScores]
    .filter((player) => {
      if (qualifierFilter === "all") return true
      const isQualifier = qualifierUserIds.has(player.playerId)
      return qualifierFilter === "qualifiers" ? isQualifier : !isQualifier
    })
    .sort((a, b) => {
      if (sortKey === "name") {
        const cmp = a.playerName.localeCompare(b.playerName)
        return sortDir === "asc" ? cmp : -cmp
      }
      return sortDir === "desc"
        ? b.totalScore - a.totalScore
        : a.totalScore - b.totalScore
    })

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle>Game {gameNumber}</CardTitle>
          {hasQualifiers && (
            <div className="flex items-center gap-1">
              <Button
                variant={qualifierFilter === "all" ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setQualifierFilter("all")}
              >
                All
              </Button>
              <Button
                variant={qualifierFilter === "qualifiers" ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setQualifierFilter("qualifiers")}
              >
                <Star className="h-3 w-3 mr-1 text-amber-500" />
                Qualifiers
              </Button>
              <Button
                variant={qualifierFilter === "non-qualifiers" ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setQualifierFilter("non-qualifiers")}
              >
                Non-qualifiers
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead
                className="sticky left-0 bg-background z-10 min-w-[200px] cursor-pointer hover:bg-muted/50"
                onClick={() => handleSort("name")}
              >
                <span className="flex items-center gap-1">
                  Player {sortKey === "name" && (sortDir === "asc" ? "↑" : "↓")}
                </span>
              </TableHead>
              <TableHead
                className="text-right min-w-[70px] font-bold bg-amber-50/50 dark:bg-amber-900/10 cursor-pointer hover:bg-amber-100/50"
                onClick={() => handleSort("total")}
              >
                <span className="flex items-center justify-end gap-1">
                  Total {sortKey === "total" && (sortDir === "asc" ? "↑" : "↓")}
                </span>
              </TableHead>
              {Array.from({ length: totalRounds }, (_, i) => {
                const roundNum = i + 1
                const isExpanded = expandedRound === roundNum
                return (
                  <TableHead
                    key={`round-${roundNum}`}
                    colSpan={isExpanded ? questionsPerRound + 1 : 1}
                    className={cn(
                      "text-center cursor-pointer hover:bg-muted/50 select-none",
                      isExpanded && "bg-muted/30"
                    )}
                    onClick={() => toggleRound(roundNum)}
                  >
                    <div className="flex items-center justify-center gap-1">
                      <ChevronRight
                        className={cn(
                          "h-3 w-3 transition-transform",
                          isExpanded && "rotate-90"
                        )}
                      />
                      R{roundNum}
                    </div>
                  </TableHead>
                )
              })}
            </TableRow>
            {expandedRound !== null && (
              <TableRow>
                <TableHead className="sticky left-0 bg-background z-10" />
                <TableHead />
                {Array.from({ length: totalRounds }, (_, i) => {
                  const roundNum = i + 1
                  const isExpanded = expandedRound === roundNum
                  if (!isExpanded) {
                    return <TableHead key={`sub-${roundNum}`} />
                  }
                  const qStart = (roundNum - 1) * questionsPerRound + 1
                  return (
                    <>
                      <TableHead key={`sub-total-${roundNum}`} />
                      {Array.from({ length: questionsPerRound }, (_, qi) => (
                        <TableHead
                          key={`q-${roundNum}-${qi}`}
                          className="text-center text-xs font-medium text-muted-foreground min-w-[32px] px-1"
                        >
                          Q{qStart + qi}
                        </TableHead>
                      ))}
                    </>
                  )
                })}
              </TableRow>
            )}
          </TableHeader>
          <TableBody>
            {sortedPlayers.map((player) => {
              const isQualifier = qualifierUserIds.has(player.playerId)
              return (
                <TableRow key={player.playerId}>
                  <TableCell className="font-medium sticky left-0 bg-background z-10">
                    <span className="flex items-center gap-1">
                      {player.playerName}
                      <CountryCell country={player.country} showName={false} />
                      {isQualifier && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Star className="h-3 w-3 text-amber-500 fill-amber-500 shrink-0" />
                          </TooltipTrigger>
                          <TooltipContent side="top">
                            <p className="text-sm">Qualifier</p>
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </span>
                  </TableCell>
                  <TableCell className="text-right font-bold tabular-nums bg-amber-50/50 dark:bg-amber-900/10">
                    {player.totalScore}
                  </TableCell>
                  {Array.from({ length: totalRounds }, (_, i) => {
                    const roundNum = i + 1
                    const isExpanded = expandedRound === roundNum
                    const roundScore = player.roundScores[roundNum]

                    if (!isExpanded) {
                      return (
                        <TableCell
                          key={`r-${roundNum}`}
                          className="text-center tabular-nums"
                        >
                          {roundScore}
                        </TableCell>
                      )
                    }

                    const roundQuestions = questionsByRound[roundNum] ?? []

                    return (
                      <>
                        <TableCell
                          key={`r-total-${roundNum}`}
                          className="text-center font-semibold tabular-nums bg-muted/20 border-l"
                        >
                          {roundScore}
                        </TableCell>
                        {Array.from({ length: questionsPerRound }, (_, qi) => {
                          const questionScore = player.questionScores.find(
                            (q) => q.roundNumber === roundNum && q.order === qi
                          )
                          const question = roundQuestions[qi]

                          if (!questionScore) {
                            return (
                              <TableCell
                                key={`q-${roundNum}-${qi}`}
                                className="text-center text-muted-foreground px-1"
                              >
                                —
                              </TableCell>
                            )
                          }

                          return (
                            <Tooltip key={`q-${roundNum}-${qi}`}>
                              <TooltipTrigger asChild>
                                <TableCell
                                  className={cn(
                                    "text-center text-xs tabular-nums cursor-help px-1",
                                    questionScore.isCorrect
                                      ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300"
                                      : "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300"
                                  )}
                                >
                                  {questionScore.score}
                                </TableCell>
                              </TooltipTrigger>
                              {question?.text && (
                                <TooltipContent side="top" className="max-w-xs">
                                  <p className="text-sm">{question.text}</p>
                                </TooltipContent>
                              )}
                            </Tooltip>
                          )
                        })}
                      </>
                    )
                  })}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}
